import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { loadRegionalBranchZipcodeAnalytics } from '../../context-api/api-services/RegionalBranchZipcodeAnalytics.service';
import { MenuProps, sharedInputSx } from '../Products/kitchenProductsConstants';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';

const REFRESH_INTERVAL_MS = 60_000;
const ZIP_FILTER_ALL = 'all';
const PAGE_MAX_WIDTH = 680;

const TAB = {
  stores: 'stores',
  customers: 'customers',
};

/** Header navy from src/composed/header/Header.js */
const HEADER_BLUE = '#181436';
const BORDER_RADIUS = '4px';
const BORDER_1PX = `.8px solid ${HEADER_BLUE}`;

const C = {
  orange: '#fc5d1b',
  ink: HEADER_BLUE,
  border: HEADER_BLUE,
  updateBarBg: '#fffbf2',
  cardBg: '#FFFFFF',
  greenDot: '#22C55E',
  tableHeadBg: HEADER_BLUE,
};

const tableHeadCellSx = {
  fontFamily: 'Inter Bold',
  fontSize: '0.9rem',
  color: '#fff',
  bgcolor: C.tableHeadBg,
  borderBottom: BORDER_1PX,
  borderRight: BORDER_1PX,
  borderTop: 'none',
  borderLeft: 'none',
  py: 1.25,
  '&:last-of-type': { borderRight: 'none' },
};

const tableBodyCellSx = {
  fontFamily: 'Inter Regular',
  fontSize: '0.9rem',
  borderBottom: BORDER_1PX,
  borderRight: BORDER_1PX,
  borderTop: 'none',
  borderLeft: 'none',
  py: 1.25,
  '&:last-of-type': { borderRight: 'none' },
};

const tableLastRowCellSx = {
  borderBottom: 'none',
};

const tabBtnBase = {
  fontSize: '15px',
  borderRadius: BORDER_RADIUS,
  cursor: 'pointer',
  padding: '4px 12px',
  marginRight: '10px',
};

const tabBtnInactive = {
  ...tabBtnBase,
  color: 'gray',
  fontFamily: 'Inter Regular',
  backgroundColor: 'transparent',
  border: `1px solid #D7E0F2`,
};

const tabBtnActive = {
  ...tabBtnBase,
  color: C.ink,
  fontFamily: 'Inter Bold',
  backgroundColor: 'white',
  border: BORDER_1PX,
  padding: '4px 12px',
};

function formatLastUpdated(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} - ${timePart}`;
}

function buildEntityTableRows(zipcodes, entityKey) {
  const rows = [];
  for (const zipRow of zipcodes) {
    const zip = String(zipRow?.zipcode ?? '').trim();
    if (!zip) continue;
    const items = Array.isArray(zipRow?.[entityKey]) ? zipRow[entityKey] : [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      rows.push({
        id: `${zip}-${entityKey}-${i}-${item?.name ?? ''}`,
        zipcode: zip,
        name: item?.name?.trim() || '—',
      });
    }
  }
  return rows.sort((a, b) => {
    const z = a.zipcode.localeCompare(b.zipcode);
    if (z !== 0) return z;
    return a.name.localeCompare(b.name);
  });
}

function countEntitiesInZip(zipRow, entityKey) {
  const items = zipRow?.[entityKey];
  return Array.isArray(items) ? items.length : 0;
}

const ZipcodeAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB.stores);
  const [selectedZip, setSelectedZip] = useState('');

  const branchName = getManagingBranchNameForApi();

  const loadAnalytics = useCallback(
    async (forceRefresh = false, { showRefreshSpinner = false } = {}) => {
      if (showRefreshSpinner) {
        setRefreshing(true);
      } else {
        setLoading((prev) => (forceRefresh ? prev : true));
      }
      setError(null);
      const result = await loadRegionalBranchZipcodeAnalytics(branchName, { forceRefresh });
      if (!result.ok) {
        setAnalytics(null);
        setLoading(false);
        setRefreshing(false);
        setError(result.error || 'Could not load zipcode analytics.');
        return;
      }
      setAnalytics(result.data);
      setLastUpdated(result.refreshedAt ?? Date.now());
      setLoading(false);
      setRefreshing(false);
      setError(null);
    },
    [branchName]
  );

  const handleReload = useCallback(() => {
    void loadAnalytics(true, { showRefreshSpinner: true });
  }, [loadAnalytics]);

  useEffect(() => {
    void loadAnalytics(false);
  }, [loadAnalytics]);

  // useEffect(() => {
  //   const id = window.setInterval(() => {
  //     void loadAnalytics(true);
  //   }, REFRESH_INTERVAL_MS);
  //   return () => window.clearInterval(id);
  // }, [loadAnalytics]);

  const zipcodes = useMemo(
    () => (Array.isArray(analytics?.zipcodes) ? analytics.zipcodes : []),
    [analytics]
  );

  const zipcodeOptions = useMemo(
    () =>
      zipcodes
        .map((z) => String(z?.zipcode ?? '').trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [zipcodes]
  );

  const entityKey = activeTab === TAB.stores ? 'stores' : 'customers';
  const entityLabel = activeTab === TAB.stores ? 'Store' : 'Customer';
  const entityLabelPlural = activeTab === TAB.stores ? 'Stores' : 'Customers';
  const nameColumnLabel = activeTab === TAB.stores ? 'Store Name' : 'Customer Name';

  const allTableRows = useMemo(
    () => buildEntityTableRows(zipcodes, entityKey),
    [zipcodes, entityKey]
  );

  const filteredTableRows = useMemo(() => {
    if (!selectedZip) return allTableRows;
    return allTableRows.filter((row) => row.zipcode === selectedZip);
  }, [allTableRows, selectedZip]);

  const selectedZipRow = useMemo(
    () => zipcodes.find((z) => String(z?.zipcode ?? '').trim() === selectedZip),
    [zipcodes, selectedZip]
  );

  const filterSummary = useMemo(() => {
    if (!selectedZip) return null;
    const count = selectedZipRow
      ? countEntitiesInZip(selectedZipRow, entityKey)
      : filteredTableRows.length;
    return { zip: selectedZip, count };
  }, [selectedZip, selectedZipRow, entityKey, filteredTableRows.length]);

  const summary = analytics?.summary ?? {};
  const totalEntities =
    activeTab === TAB.stores ? summary.totalStores : summary.totalCustomers;
  const zipcodeCount = summary.zipcodeCount ?? zipcodeOptions.length;

  return (
    <Box
      sx={{
        // bgcolor: '#F8FAFC',
        minHeight: 'calc(100vh - 50px)',
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 4 },
        maxWidth: PAGE_MAX_WIDTH,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Inter Bold, sans-serif',
            fontSize: '22px',
            fontWeight: 700,
            color: C.ink,
            lineHeight: 1.2,
            m: 0,
          }}
        >
          Analytics
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter Regular, sans-serif',
            fontSize: '13px',
            color: '#6b7280',
            mt: 0.5,
          }}
        >
          Zipcode insights
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
        <button
          type="button"
          style={activeTab === TAB.stores ? tabBtnActive : tabBtnInactive}
          onClick={() => {
            setActiveTab(TAB.stores);
            setSelectedZip('');
          }}
        >
          Stores
        </button>
        <button
          type="button"
          style={activeTab === TAB.customers ? tabBtnActive : tabBtnInactive}
          onClick={() => {
            setActiveTab(TAB.customers);
            setSelectedZip('');
          }}
        >
          Customers
        </button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          py: 1.5,
          px: 2,
          textAlign: 'center',
          borderRadius: BORDER_RADIUS,
          border: BORDER_1PX,
          bgcolor: C.updateBarBg,
        }}
      >
        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.95rem', color: C.ink }}>
          Updates occur every 1 minute
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mt: 0.5,
          }}
        >
          <Typography sx={{ fontFamily: 'Inter Regular', fontSize: '0.82rem', color: C.ink }}>
            Last updated : {formatLastUpdated(lastUpdated)}
          </Typography>
          <Button
            type="button"
            size="small"
            variant="outlined"
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleReload}
            disabled={refreshing}
            sx={{
              fontFamily: 'Inter Bold',
              fontSize: '0.75rem',
              textTransform: 'none',
              color: C.ink,
              borderColor: C.ink,
              borderWidth: '1px',
              borderRadius: BORDER_RADIUS,
              py: 0.25,
              minHeight: 28,
              '&:hover': {
                borderColor: C.ink,
                bgcolor: 'rgba(24, 20, 54, 0.06)',
              },
            }}
          >
            {refreshing ? 'Reloading…' : 'Reload'}
          </Button>
        </Box>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: BORDER_RADIUS }}>
          {error}
        </Alert>
      ) : null}

      {Array.isArray(summary.invalidZipcodes) && summary.invalidZipcodes.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: BORDER_RADIUS }}>
          Some serving zips could not be normalized: {summary.invalidZipcodes.join(', ')}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2,
          mb: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: { xs: '1 1 100%', sm: '0 0 64%' },
            maxWidth: { xs: '100%', sm: '64%' },
            p: 2,
            borderRadius: BORDER_RADIUS,
            border: BORDER_1PX,
            bgcolor: C.cardBg,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.95rem', color: C.ink, mb: 1 }}>
            Total {entityLabelPlural}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: C.greenDot,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '1.75rem', color: C.ink }}>
              {totalEntities ?? 0}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: { xs: '1 1 100%', sm: '1 1 auto' },
            minWidth: 0,
            p: 2,
            borderRadius: BORDER_RADIUS,
            border: BORDER_1PX,
            bgcolor: C.cardBg,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.95rem', color: C.ink, mb: 1 }}>
            Zipcode
          </Typography>
          <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '1.75rem', color: C.ink }}>
            {zipcodeCount ?? 0}
          </Typography>
        </Paper>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl
          size="small"
          sx={{
            width: '100%',
            maxWidth: 220,
            ...sharedInputSx,
            '& .MuiOutlinedInput-root': {
              borderRadius: BORDER_RADIUS,
            },
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
          }}
        >
          <InputLabel id="zipcode-filter-label">Filter by zipcode</InputLabel>
          <Select
            labelId="zipcode-filter-label"
            id="zipcode-filter"
            value={selectedZip || ZIP_FILTER_ALL}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedZip(value === ZIP_FILTER_ALL ? '' : value);
            }}
            input={<OutlinedInput label="Filter by zipcode" />}
            MenuProps={MenuProps}
          >
            <MenuItem value={ZIP_FILTER_ALL}>All zipcodes</MenuItem>
            {zipcodeOptions.map((zip) => (
              <MenuItem key={zip} value={zip}>
                {zip}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filterSummary ? (
          <Typography
            component="span"
            sx={{ fontFamily: 'Inter Bold', fontSize: '0.95rem', color: C.ink }}
          >
            {entityLabel} in {filterSummary.zip} -{' '}
            <Box
              component="span"
              sx={{
                color: C.orange,
                fontFamily: 'Inter Bold',
                fontSize: '1.15rem',
              }}
            >
              {filterSummary.count}
            </Box>
          </Typography>
        ) : null}
      </Box>

      {loading && !analytics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: C.orange }} />
        </Box>
      ) : filteredTableRows.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontFamily: 'Inter Regular', color: '#94A3B8' }}>
            {selectedZip
              ? `No ${entityLabelPlural.toLowerCase()} in zip ${selectedZip}.`
              : `No ${entityLabelPlural.toLowerCase()} available.`}
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: BORDER_1PX,
            borderRadius: BORDER_RADIUS,
            overflow: 'hidden',
          }}
        >
          <Table
            size="small"
            sx={{
              borderCollapse: 'collapse',
              width: '100%',
              '& .MuiTableCell-root': {
                borderColor: C.border,
                borderTop: 'none',
                borderLeft: 'none',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Zipcode</TableCell>
                <TableCell sx={tableHeadCellSx}>{nameColumnLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTableRows.map((row, index) => {
                const isLastRow = index === filteredTableRows.length - 1;
                const rowCellSx = isLastRow
                  ? { ...tableBodyCellSx, ...tableLastRowCellSx }
                  : tableBodyCellSx;
                return (
                  <TableRow key={row.id}>
                    <TableCell sx={rowCellSx}>{row.zipcode}</TableCell>
                    <TableCell sx={rowCellSx}>{row.name}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ZipcodeAnalytics;
